// Pure maths for IIL employee advances. No Firestore/React.
//
// An advance is money ALM pays an IIL employee on Safdar's request and then
// recovers from the employee's salary at payroll. It is never billed to IIL.
//   outstanding = amount - recoveries posted by completed payrolls
//   recovery at payroll = what is due (all of it, or the instalment), never
//   more than what is left of the salary after EOBI and any other deduction.

export const EOBI_DEDUCTION = 400;

export const round = (value) => Math.round(Number(value || 0));

// Same rule the IIL payroll uses to match employee codes.
export function normalizeCode(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const number = Number(text);
  return Number.isFinite(number) ? String(Math.trunc(number)) : text;
}

export const isActiveAdvance = (row) => row.entryStatus !== "REJECTED";
export const isActiveRecovery = (row) => row.status !== "REVERSED";

const byAge = (a, b) => `${a.givenDate} ${String(a.seq || 0).padStart(15, "0")}`.localeCompare(`${b.givenDate} ${String(b.seq || 0).padStart(15, "0")}`);

// Every non-rejected advance with what has been recovered so far. Recoveries of
// `excludeMonthKey` are ignored, so the month being built is never counted
// against itself.
export function advancePositions({ advances, recoveries, excludeMonthKey = "" }) {
  const recovered = new Map();
  recoveries.filter(isActiveRecovery).filter((row) => row.monthKey !== excludeMonthKey).forEach((row) => {
    recovered.set(row.advanceId, (recovered.get(row.advanceId) || 0) + Number(row.amount || 0));
  });
  return advances.filter(isActiveAdvance).sort(byAge).map((advance) => {
    const done = round(recovered.get(advance.id) || 0);
    const outstanding = Math.max(0, round(advance.amount) - done);
    return { advance, recovered: done, outstanding, status: outstanding === 0 ? "CLEARED" : done > 0 ? "PART_RECOVERED" : "OPEN" };
  });
}

// Totals per employee code.
export function employeeBalances(positions) {
  const byCode = new Map();
  positions.forEach((position) => {
    const code = normalizeCode(position.advance.employeeCode);
    const entry = byCode.get(code) || { code, name: position.advance.employeeName, given: 0, recovered: 0, outstanding: 0, positions: [] };
    entry.given += round(position.advance.amount);
    entry.recovered += position.recovered;
    entry.outstanding += position.outstanding;
    entry.positions.push(position);
    byCode.set(code, entry);
  });
  return byCode;
}

function dueOf(position) {
  const installment = Number(position.advance.installment || 0);
  return installment > 0 ? Math.min(installment, position.outstanding) : position.outstanding;
}

// Works out the advance recovery for each employee in this payroll.
//   payrollByCode: Map code -> { gross, eobi, otherDeduction }
//   overrides:     Map/obj code -> amount the operator typed instead
// Returns { byCode, notInPayroll } where byCode has, per employee with
// something outstanding: amount, allocations [{ advanceId, amount }],
// outstanding (before), balanceAfter, capped (salary could not cover it all).
export function planRecoveries({ positions, payrollByCode, overrides = {} }) {
  const balances = employeeBalances(positions.filter((position) => position.outstanding > 0));
  const byCode = new Map();
  const notInPayroll = [];

  balances.forEach((balance, code) => {
    const payroll = payrollByCode.get(code);
    if (!payroll) { notInPayroll.push(balance); return; }
    const open = [...balance.positions].sort((a, b) => byAge(a.advance, b.advance));
    const capacity = Math.max(0, round(payroll.gross) - round(payroll.eobi) - round(payroll.otherDeduction));
    const due = open.reduce((sum, position) => sum + dueOf(position), 0);
    const typed = overrides instanceof Map ? overrides.get(code) : overrides[code];
    const overridden = typed !== undefined && typed !== null && typed !== "";
    const target = overridden ? Math.min(Math.max(round(typed), 0), balance.outstanding) : Math.min(due, capacity);

    let remaining = target;
    const allocations = [];
    open.forEach((position) => {
      if (remaining <= 0) return;
      const limit = overridden ? position.outstanding : dueOf(position);
      const take = Math.min(limit, remaining);
      if (take > 0) { allocations.push({ advanceId: position.advance.id, amount: take }); remaining -= take; }
    });
    // If instalments capped what could be taken but the typed amount is larger, finish from the rest.
    if (overridden && remaining > 0) {
      open.forEach((position) => {
        if (remaining <= 0) return;
        const already = allocations.find((item) => item.advanceId === position.advance.id)?.amount || 0;
        const take = Math.min(position.outstanding - already, remaining);
        if (take > 0) {
          const existing = allocations.find((item) => item.advanceId === position.advance.id);
          if (existing) existing.amount += take; else allocations.push({ advanceId: position.advance.id, amount: take });
          remaining -= take;
        }
      });
    }
    const amount = allocations.reduce((sum, item) => sum + item.amount, 0);
    byCode.set(code, {
      code, name: balance.name, amount, allocations, outstanding: balance.outstanding,
      balanceAfter: balance.outstanding - amount, due, capacity, overridden,
      capped: !overridden && due > capacity,
      leavesNegativeNet: amount > capacity
    });
  });
  return { byCode, notInPayroll };
}

export function validateAdvance(input) {
  if (!String(input.employeeCode || "").trim()) return "Choose the employee.";
  if (!(round(input.amount) > 0)) return "Enter an advance amount greater than zero.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(input.givenDate || ""))) return "Enter the date the advance was given.";
  if (input.installment !== "" && input.installment != null && !(round(input.installment) > 0)) return "The instalment must be greater than zero, or left blank to recover in full.";
  if (input.installment && round(input.installment) > round(input.amount)) return "The instalment cannot be more than the advance.";
  return "";
}
